import { useEffect, useState, useCallback } from "react";
import { http } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { Landmark, Clock, RefreshCcw, ArrowUpRight, CircleAlert, PoundSterling, DollarSign, Loader2, Receipt } from "lucide-react";

const CURRENCY_SYMBOL = { gbp: "£", usd: "$", eur: "€" };

function fmtMoney(amountMinor, currency = "gbp") {
  const sym = CURRENCY_SYMBOL[currency?.toLowerCase()] || "";
  const v = (amountMinor / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${sym}${v}`;
}

function fmtDate(unixSeconds) {
  if (!unixSeconds) return "—";
  return new Date(unixSeconds * 1000).toLocaleDateString(undefined, {
    year: "numeric", month: "short", day: "numeric",
  });
}

function statusBadge(status) {
  const map = {
    paid:       { label: "Paid",       cls: "bg-emerald-500" },
    in_transit: { label: "In transit", cls: "bg-indigo-500" },
    pending:    { label: "Pending",    cls: "bg-amber-500" },
    failed:     { label: "Failed",     cls: "bg-red-500" },
    canceled:   { label: "Canceled",   cls: "bg-zinc-500" },
  };
  const m = map[status] || { label: status || "—", cls: "bg-zinc-500" };
  return <Badge className={`${m.cls} text-white`}>{m.label}</Badge>;
}

export default function Payouts() {
  const [venue, setVenue] = useState(null);
  const [balance, setBalance] = useState(null);
  const [payouts, setPayouts] = useState(null);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [openPayoutId, setOpenPayoutId] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    setRefreshing(true);
    try {
      const [v, b, p] = await Promise.all([
        http.get("/venues/me"),
        http.get("/venues/me/stripe/balance").catch((e) => ({ __error: e })),
        http.get("/venues/me/stripe/payouts").catch((e) => ({ __error: e })),
      ]);
      setVenue(v.data);
      if (b.__error) {
        // Not-yet-onboarded (400) is expected — surface a specific state
        setBalance(null);
        setError(b.__error?.response?.data?.detail || "Could not load balance");
      } else {
        setBalance(b.data);
      }
      if (p.__error) {
        setPayouts(null);
      } else {
        setPayouts(p.data);
      }
    } catch (e) {
      setError("Could not load payouts");
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!venue) return <div className="p-10 text-sm text-zinc-500">Loading…</div>;

  const currency = (venue.country === "US" ? "usd" : "gbp");
  const CurrencyIcon = venue.country === "US" ? DollarSign : PoundSterling;

  const availableTotal = (balance?.available || [])
    .filter((b) => b.currency === currency)
    .reduce((s, b) => s + b.amount, 0);
  const pendingTotal = (balance?.pending || [])
    .filter((b) => b.currency === currency)
    .reduce((s, b) => s + b.amount, 0);

  const notConnected = !venue.stripe_account_id;

  return (
    <div className="p-8 lg:p-10 max-w-5xl" data-testid="payouts-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs text-zinc-500 uppercase tracking-widest font-medium">Money</div>
          <h1 className="mt-1 font-display font-black text-3xl lg:text-4xl tracking-tight">Payouts</h1>
          <p className="mt-2 text-sm text-zinc-600 max-w-lg">
            Ticket revenue lands in your Stripe balance, then Stripe pays it out to your bank on their normal schedule. Card fees are on us — you never see them here.
          </p>
        </div>
        <Button
          data-testid="payouts-refresh-btn"
          variant="outline"
          onClick={load}
          disabled={refreshing}
          className="rounded-full"
        >
          <RefreshCcw className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {notConnected && (
        <div
          data-testid="payouts-not-connected"
          className="mt-8 rounded-2xl border border-amber-200 bg-amber-50 p-6 flex items-start gap-4"
        >
          <span className="w-10 h-10 grid place-items-center rounded-full bg-amber-500 text-white shrink-0">
            <CircleAlert className="w-5 h-5" />
          </span>
          <div>
            <div className="font-display font-bold text-lg">Connect Stripe to see payouts</div>
            <p className="mt-1 text-sm text-zinc-700">
              You&apos;ll only see money here once your Stripe account is connected. Head to <a href="/dashboard/settings" className="underline">Branding &amp; Settings</a> to connect it — takes about a minute.
            </p>
          </div>
        </div>
      )}

      {!notConnected && (
        <>
          {/* BALANCE — two side-by-side cards */}
          <div className="mt-8 grid sm:grid-cols-2 gap-4" data-testid="payouts-balance-grid">
            {/* Available */}
            <div
              data-testid="balance-available-card"
              className="rounded-2xl border border-zinc-200 bg-white p-6"
            >
              <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-emerald-600">
                <Landmark className="w-3.5 h-3.5" /> Available to pay out
              </div>
              <div className="mt-3 font-display font-black text-4xl tracking-tighter">
                {balance ? fmtMoney(availableTotal, currency) : <span className="text-zinc-300">—</span>}
              </div>
              <div className="mt-1 text-xs text-zinc-500">
                Ready for the next automatic payout to your bank.
              </div>
            </div>

            {/* Pending */}
            <div
              data-testid="balance-pending-card"
              className="rounded-2xl border border-zinc-200 bg-white p-6"
            >
              <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-indigo-600">
                <Clock className="w-3.5 h-3.5" /> Pending
              </div>
              <div className="mt-3 font-display font-black text-4xl tracking-tighter">
                {balance ? fmtMoney(pendingTotal, currency) : <span className="text-zinc-300">—</span>}
              </div>
              <div className="mt-1 text-xs text-zinc-500">
                Recently sold tickets clearing through Stripe. Moves to &ldquo;Available&rdquo; automatically.
              </div>
            </div>
          </div>

          {error && !balance && (
            <div className="mt-4 rounded-xl bg-red-50 border border-red-200 p-4 text-sm text-red-700 flex items-center gap-2">
              <CircleAlert className="w-4 h-4" /> {error}
            </div>
          )}

          {/* PAYOUTS TABLE */}
          <div className="mt-10">
            <div className="flex items-baseline justify-between mb-4">
              <h2 className="font-display font-bold text-xl tracking-tight">Recent payouts</h2>
              <span className="text-xs text-zinc-500 flex items-center gap-1">
                <CurrencyIcon className="w-3.5 h-3.5" /> {currency.toUpperCase()}
              </span>
            </div>

            <div
              data-testid="payouts-table"
              className="rounded-2xl border border-zinc-200 bg-white overflow-hidden"
            >
              {payouts?.data?.length ? (
                <table className="w-full text-sm">
                  <thead className="bg-zinc-50 border-b border-zinc-200">
                    <tr className="text-left text-xs font-mono uppercase tracking-widest text-zinc-500">
                      <th className="px-5 py-3">Date</th>
                      <th className="px-5 py-3">Amount</th>
                      <th className="px-5 py-3">Arrival</th>
                      <th className="px-5 py-3">Status</th>
                      <th className="px-5 py-3">Method</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payouts.data.map((p) => (
                      <tr
                        key={p.id}
                        data-testid={`payout-row-${p.id}`}
                        onClick={() => setOpenPayoutId(p.id)}
                        className="border-b border-zinc-100 last:border-b-0 hover:bg-zinc-50 transition-colors cursor-pointer"
                      >
                        <td className="px-5 py-4 font-medium text-zinc-900">{fmtDate(p.created)}</td>
                        <td className="px-5 py-4 font-mono">{fmtMoney(p.amount, p.currency)}</td>
                        <td className="px-5 py-4 text-zinc-600">{fmtDate(p.arrival_date)}</td>
                        <td className="px-5 py-4">{statusBadge(p.status)}</td>
                        <td className="px-5 py-4 text-zinc-600 capitalize">
                          {p.type?.replace("_", " ") || "—"}
                          {p.method === "instant" ? " · instant" : ""}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div
                  data-testid="payouts-empty"
                  className="p-10 text-center"
                >
                  <div className="w-12 h-12 mx-auto grid place-items-center rounded-full bg-zinc-100">
                    <Landmark className="w-5 h-5 text-zinc-500" />
                  </div>
                  <div className="mt-4 font-display font-bold text-lg">No payouts yet</div>
                  <p className="mt-2 text-sm text-zinc-600 max-w-sm mx-auto">
                    As soon as you sell tickets, Stripe will start scheduling payouts to your bank. They&apos;ll show up here automatically.
                  </p>
                </div>
              )}
            </div>

            <p className="mt-4 text-xs text-zinc-500 flex items-center gap-1">
              <ArrowUpRight className="w-3 h-3" />
              Payouts follow Stripe&apos;s standard schedule for your account (usually 2–7 days after a ticket is sold, depending on region).
            </p>
          </div>
        </>
      )}

      <PayoutDetailDrawer
        payoutId={openPayoutId}
        currency={currency}
        onClose={() => setOpenPayoutId(null)}
      />
    </div>
  );
}

function txTypeLabel(t) {
  const map = {
    charge:          { label: "Ticket sale",       cls: "text-emerald-600" },
    payment:         { label: "Ticket sale",       cls: "text-emerald-600" },
    refund:          { label: "Refund",            cls: "text-amber-600" },
    payment_refund:  { label: "Refund",            cls: "text-amber-600" },
    adjustment:      { label: "Adjustment",        cls: "text-zinc-600" },
    application_fee: { label: "Platform fee",      cls: "text-zinc-500" },
    application_fee_refund: { label: "Fee refund", cls: "text-zinc-500" },
    stripe_fee:      { label: "Stripe fee",        cls: "text-zinc-500" },
    payout:          { label: "Payout",            cls: "text-indigo-600" },
  };
  return map[t] || { label: t?.replace(/_/g, " ") || "—", cls: "text-zinc-600" };
}

function PayoutDetailDrawer({ payoutId, currency, onClose }) {
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!payoutId) { setDetail(null); return; }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const { data } = await http.get(`/venues/me/stripe/payouts/${payoutId}`);
        if (!cancelled) setDetail(data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Could not load payout");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [payoutId]);

  const open = !!payoutId;

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent data-testid="payout-drawer" className="w-full sm:max-w-2xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Payout detail</SheetTitle>
          <SheetDescription>
            Every ticket sale, refund and fee that made up this deposit to your bank.
          </SheetDescription>
        </SheetHeader>

        {loading && (
          <div className="mt-10 flex items-center gap-2 text-sm text-zinc-500">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading transactions…
          </div>
        )}

        {error && (
          <div className="mt-6 rounded-xl bg-red-50 border border-red-200 p-4 text-sm text-red-700 flex items-center gap-2">
            <CircleAlert className="w-4 h-4" /> {error}
          </div>
        )}

        {!loading && detail && (
          <>
            {/* Summary card */}
            <div className="mt-6 rounded-2xl border border-zinc-200 bg-zinc-50 p-5" data-testid="payout-summary-card">
              <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">Amount paid out</div>
              <div className="mt-1 font-display font-black text-3xl tracking-tighter">
                {fmtMoney(detail.amount, detail.currency || currency)}
              </div>
              <div className="mt-3 grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-xs text-zinc-500">Created</div>
                  <div className="font-medium">{fmtDate(detail.created)}</div>
                </div>
                <div>
                  <div className="text-xs text-zinc-500">Arrives</div>
                  <div className="font-medium">{fmtDate(detail.arrival_date)}</div>
                </div>
                <div>
                  <div className="text-xs text-zinc-500">Status</div>
                  <div>{statusBadge(detail.status)}</div>
                </div>
                <div>
                  <div className="text-xs text-zinc-500">Method</div>
                  <div className="font-medium capitalize">
                    {detail.type?.replace("_", " ") || "—"}
                    {detail.method === "instant" ? " · instant" : ""}
                  </div>
                </div>
              </div>
            </div>

            {/* Transactions */}
            <div className="mt-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-display font-bold text-base tracking-tight flex items-center gap-2">
                  <Receipt className="w-4 h-4" /> What&apos;s in this payout
                </h3>
                <span className="text-xs text-zinc-500">{detail.transactions?.length || 0} lines</span>
              </div>

              {(detail.transactions?.length || 0) === 0 ? (
                <div className="rounded-xl border border-zinc-200 p-8 text-center text-sm text-zinc-500">
                  No transaction lines on this payout.
                </div>
              ) : (
                <div className="rounded-xl border border-zinc-200 overflow-hidden divide-y divide-zinc-100" data-testid="payout-tx-list">
                  {detail.transactions.map((t) => {
                    const lbl = txTypeLabel(t.type);
                    const isNegative = t.amount < 0;
                    return (
                      <div key={t.id} className="p-4 flex items-start gap-4">
                        <div className="flex-1 min-w-0">
                          <div className={`text-xs font-mono uppercase tracking-widest ${lbl.cls}`}>
                            {lbl.label}
                          </div>
                          <div className="mt-0.5 text-sm text-zinc-900 truncate">
                            {t.description || t.id}
                          </div>
                          {t.order && (
                            <div className="mt-1 text-xs text-zinc-500">
                              Order <span className="font-mono">{t.order.order_number}</span>
                              {t.order.buyer_email ? ` · ${t.order.buyer_email}` : ""}
                            </div>
                          )}
                        </div>
                        <div className="text-right shrink-0">
                          <div className={`text-sm font-mono font-medium ${isNegative ? "text-amber-700" : "text-zinc-900"}`}>
                            {isNegative ? "−" : "+"}
                            {fmtMoney(Math.abs(t.amount), detail.currency || currency)}
                          </div>
                          {t.fee > 0 && (
                            <div className="text-xs text-zinc-500 mt-0.5">
                              fee −{fmtMoney(t.fee, detail.currency || currency)}
                            </div>
                          )}
                          <div className="text-xs text-zinc-500 mt-0.5">
                            net {fmtMoney(t.net, detail.currency || currency)}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
